/** Cartão de uma camada GOES da pilha: produto, visibilidade, opacidade, ordem. */
import clsx from 'clsx';
import { ArrowDown, ArrowUp, Eye, EyeOff, Replace, Trash2 } from 'lucide-react';
import { getProduct, MAX_GOES_LAYERS } from '../config/products';
import { useCatalogStore } from '../goes/catalog';
import { formatDayTimeBrt } from '../lib/time';
import { usePlanStore } from '../loop/plan';
import { useGoesStore, type GoesLayerState } from '../state/goes';
import { showToast } from '../state/toasts';
import { DayOnlyBadge } from './ProductPicker';
import { useSemBaseNoQuadro } from './hooks';
import { ProductThumbnail } from './Thumbnail';
import { Button, IconButton, Slider } from './primitives';

/** Alvos de toque de 44 px em telas de toque. */
const TOUCH = 'pointer-coarse:size-11';

/** Remove a camada com opção de desfazer (toast). */
function removeWithUndo(layer: GoesLayerState, position: number): void {
  const store = useGoesStore.getState();
  if (store.layers.length <= 1) return;
  store.removeLayer(layer.uid);
  showToast({
    id: `removed-${layer.uid}`,
    kind: 'info',
    message: `Camada “${getProduct(layer.productId)?.name ?? layer.productId}” removida.`,
    timeoutMs: 8000,
    action: {
      label: 'Desfazer',
      onClick: () => {
        const layers = [...useGoesStore.getState().layers];
        if (layers.some((l) => l.uid === layer.uid) || layers.length >= MAX_GOES_LAYERS) return;
        layers.splice(Math.min(position, layers.length), 0, layer);
        useGoesStore.getState().setLayers(layers);
      },
    },
  });
}

function positionLabel(position: number, count: number): string {
  if (count === 1) return 'Imagem';
  if (position === 0) return 'Base · define os horários';
  if (position === count - 1) return 'Camada de cima';
  return 'Camada do meio';
}

export function LayerCard(props: {
  layer: GoesLayerState;
  /** Posição na pilha (0 = base). */
  position: number;
  count: number;
  onChangeProduct: () => void;
}) {
  const { layer, position, count, onChangeProduct } = props;
  const product = getProduct(layer.productId);
  const { updateLayer, moveLayer } = useGoesStore.getState();
  const status = useCatalogStore((s) => s.entries[layer.productId]?.status);
  const hasListing = useCatalogStore((s) => !!s.entries[layer.productId]?.listing);
  // Só muda quando a situação da camada no quadro muda (não re-renderiza a cada quadro).
  const frameStatus = usePlanStore((s) => s.frameStatus[layer.uid]);
  // Enquanto o aviso central do mapa está na tela, ele é o aviso principal da camada de base.
  const overlayShown = usePlanStore((s) => !s.firstFrameShown) && position === 0;
  // A linha do tempo segue sem a base neste quadro (loop/semNoaa.ts).
  const semBaseAqui = useSemBaseNoQuadro();
  const semBase = position === 0 ? semBaseAqui : null;

  return (
    <li
      className={clsx(
        'rounded-xl border bg-ink-850/60 p-3 transition-opacity',
        layer.visible ? 'border-ink-700' : 'border-ink-700/60 opacity-70',
      )}
      aria-label={`${positionLabel(position, count)}: ${product?.name ?? layer.productId}`}
    >
      <div className="flex gap-3">
        <button
          type="button"
          onClick={onChangeProduct}
          title="Trocar a imagem desta camada"
          className="shrink-0 rounded-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mata-400"
        >
          <ProductThumbnail productId={layer.productId} width={76} height={46} className="h-[46px] w-[76px] rounded-md" />
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] font-medium text-ink-300">
              {positionLabel(position, count)}
            </span>
            {product?.dayOnly && <DayOnlyBadge />}
          </div>
          <div className="truncate text-sm font-medium text-white">{product?.name ?? layer.productId}</div>
          <div className="truncate text-[11px] text-ink-300">{product?.technical}</div>
        </div>
        <IconButton
          icon={layer.visible ? Eye : EyeOff}
          label={layer.visible ? 'Esconder camada' : 'Mostrar camada'}
          size="sm"
          active={layer.visible}
          onClick={() => updateLayer(layer.uid, { visible: !layer.visible })}
          className={TOUCH}
        />
      </div>

      {/* Uma explicação curta sempre visível (2 linhas); o texto completo fica no título e no catálogo. */}
      {product && (
        <p className="mt-2 line-clamp-2 text-xs leading-snug text-ink-300" title={product.description}>
          {product.description}
        </p>
      )}

      <div className="mt-3">
        <Slider
          label="Opacidade"
          value={Math.round(layer.opacity * 100)}
          min={0}
          max={100}
          step={5}
          format={(v) => `${v}%`}
          onChange={(v) => updateLayer(layer.uid, { opacity: v / 100 })}
        />
      </div>

      <div className="mt-2.5 flex items-center gap-1">
        <Button size="sm" icon={Replace} onClick={onChangeProduct}>
          Trocar imagem
        </Button>
        <span className="flex-1" />
        <IconButton
          icon={ArrowUp}
          size="sm"
          label="Subir camada (desenhar por cima)"
          disabled={position >= count - 1}
          onClick={() => moveLayer(layer.uid, 1)}
          className={TOUCH}
        />
        <IconButton
          icon={ArrowDown}
          size="sm"
          label="Descer camada (desenhar por baixo)"
          disabled={position === 0}
          onClick={() => moveLayer(layer.uid, -1)}
          className={TOUCH}
        />
        <IconButton
          icon={Trash2}
          size="sm"
          label="Remover camada"
          disabled={count <= 1}
          onClick={() => removeWithUndo(layer, position)}
          className={TOUCH}
        />
      </div>

      <LayerStatusLine
        message={
          !hasListing && status === 'error' && !overlayShown
            ? 'Não foi possível buscar esta imagem na NOAA. Tentando de novo…'
            : !hasListing && status === 'loading' && !overlayShown
              ? 'Buscando a lista de imagens…'
              : layer.visible && frameStatus === 'failed'
                ? 'Imagem indisponível neste horário (a NOAA não entregou este quadro). A camada fica escondida neste quadro.'
                : layer.visible && frameStatus === 'none' && semBase
                  ? semBase.noFim
                    ? `Parada na NOAA desde ${formatDayTimeBrt(semBase.desde)}. As outras camadas seguem no horário; esta volta sozinha quando a NOAA publicar.`
                    : `A NOAA não publicou esta imagem entre ${formatDayTimeBrt(semBase.desde)} e ${formatDayTimeBrt(semBase.ate)}. As outras camadas seguem no horário.`
                  : layer.visible && frameStatus === 'none'
                    ? 'Sem imagem desta camada neste horário (ela fica escondida neste quadro).'
                    : null
        }
        warn={(!hasListing && status === 'error') || frameStatus === 'failed' || (frameStatus === 'none' && !!semBase)}
      />
    </li>
  );
}

function LayerStatusLine(props: { message: string | null; warn: boolean }) {
  if (!props.message) return null;
  return (
    <p className={clsx('mt-2 text-[11px] leading-snug', props.warn ? 'text-yellow-300' : 'text-ink-300')} role="status">
      {props.message}
    </p>
  );
}
